import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Progress } from "@/shared/ui/progress";
import { StatusBadge } from "@/shared/ui/StatusBadge";
// 及格线与 H5 结果卡同源(scoreGrade.PASS_SCORE),别再抄一份数字。
import { PASS_SCORE } from "@/wecom-h5/coaching/scoreGrade";
import { coachingService, type TranscriptTurnScore } from "../../api/coachingService";
import { fmtDatetime, gradeColor, pdfStatusLabel, pdfStatusType, scoreTone } from "./helpers";

type Props = {
  evalId: number | null;
  onClose: () => void;
};

/** 终评每个维度的满分(ai 侧 0–20 尺度)。 */
const DIMENSION_MAX = 20;

/** 终评里一个维度的形状:{score 0–20, comment, name?}。后端 Map<String,Object> 没类型,这里收窄。 */
type DimensionEntry = { score: number; comment: string | null; name: string | null };

function toDimensionEntries(raw: Record<string, unknown> | null): [string, DimensionEntry][] {
  if (!raw) return [];
  const rows: [string, DimensionEntry][] = [];
  for (const [key, v] of Object.entries(raw)) {
    if (!v || typeof v !== "object") continue;
    const o = v as { score?: unknown; comment?: unknown; name?: unknown; label?: unknown };
    // `score: null` = 本场未涉及,不进列表。★先判 null 再转数:Number(null) 是 0,会显成一条「0/20」。
    if (o.score == null || o.score === "") continue;
    const score = typeof o.score === "number" ? o.score : Number(o.score);
    if (!Number.isFinite(score)) continue;
    const name = [o.name, o.label].find((x): x is string => typeof x === "string" && x.length > 0);
    rows.push([
      key,
      {
        score,
        comment: typeof o.comment === "string" && o.comment ? o.comment : null,
        name: name ?? null,
      },
    ]);
  }
  // 按分从低到高:经理先看到最该讲评的那一维(与逐轮点评同一排法)
  return rows.sort((a, b) => a[1].score - b[1].score);
}

/**
 * 维度得分(0829 陪练评分复盘 P2):终评五维每维 0–20 → 一行 = 中文名 + 分条 + 点评。
 * `knowledge_accuracy` 是「员工发言与知识库原文的向量相似度」,不是讲对没讲错 —— 单独标「知识匹配」,
 * 别让人把它当成知识考核分。维度名走 i18n `dimensionNames.*`;场景自定义键没有译文时退回后端带的
 * name,再没有就原样显 key(不崩、不空)。
 */
function DimensionScores({ raw }: { raw: Record<string, unknown> | null }) {
  const { t } = useTranslation("coaching");
  const rows = toDimensionEntries(raw);
  if (!rows.length) return null;
  return (
    <div data-testid="eval-dimensions">
      <p className="text-xs text-muted-foreground mb-2">{t("evaluationDetail.dimensions")}</p>
      <ul className="space-y-2">
        {rows.map(([key, d]) => {
          const label = t(`dimensionNames.${key}`, { defaultValue: d.name ?? key });
          const pct = Math.max(0, Math.min(100, Math.round((d.score / DIMENSION_MAX) * 100)));
          return (
            <li key={key} data-testid={`dim-${key}`} className="text-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 truncate font-medium">{label}</span>
                {key === "knowledge_accuracy" && (
                  <StatusBadge type="info">{t("evaluationDetail.knowledgeMatch")}</StatusBadge>
                )}
                <span className="ml-auto shrink-0 tabular-nums">{`${d.score}/${DIMENSION_MAX}`}</span>
              </div>
              <Progress
                value={pct}
                aria-label={label}
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                className="mt-1 h-1.5"
              />
              {d.comment && (
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{d.comment}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** 一段要点列表(做得好的 / 待改进)。空数组整块不出现 —— 不留空标题。 */
function FeedbackList({
  label,
  lines,
  accentClass,
}: {
  label: string;
  lines: string[];
  accentClass: string;
}) {
  if (!lines?.length) return null;
  return (
    <div className={`border-l-2 pl-3 ${accentClass}`}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <ul className="space-y-1 text-sm leading-relaxed">
        {lines.map((line) => (
          <li key={line}>· {line}</li>
        ))}
      </ul>
    </div>
  );
}

/** 逐轮点评。按扣分从多到少排 —— 经理先看到最该讲评的那一轮,不用自己扫一遍。 */
function TurnScoreList({ label, rows }: { label: string; rows: TranscriptTurnScore[] }) {
  if (!rows?.length) return null;
  const sorted = [...rows].sort((a, b) => a.score - b.score);
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <ul className="space-y-1">
        {sorted.map((r) => (
          <li key={`${r.turn}-${r.dimension}`} className="flex items-baseline gap-2 text-sm">
            <span className="text-xs text-muted-foreground tabular-nums shrink-0">
              {`#${r.turn}`}
            </span>
            <span className="font-medium tabular-nums shrink-0">{`${r.score}/20`}</span>
            <span className="text-muted-foreground truncate">{r.comment ?? r.dimension}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EvaluationDetailDialog({ evalId, onClose }: Props) {
  const { t } = useTranslation("coaching");
  const {
    data: detail,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["coaching-eval-detail", evalId],
    queryFn: () => coachingService.getEvaluationDetail(evalId!),
    enabled: evalId !== null,
    meta: { suppressGlobalErrorToast: true },
  });

  return (
    <Dialog open={evalId !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("evaluationDetail.title")}</DialogTitle>
        </DialogHeader>
        {detail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("evaluationDetail.employeeName")}
                </p>
                <p className="text-sm font-medium">{detail.employeeName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.dept")}</p>
                <p className="text-sm font-medium">{detail.dept ?? "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.taskName")}</p>
                <p className="text-sm font-medium">{detail.taskName ?? "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("evaluationDetail.scenarioName")}
                </p>
                <p className="text-sm font-medium">{detail.scenarioName ?? "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.completedAt")}</p>
                <p className="text-sm font-medium">{fmtDatetime(detail.completedAt)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.pdfStatus")}</p>
                <StatusBadge type={pdfStatusType(detail.pdfStatus)}>
                  {pdfStatusLabel(detail.pdfStatus)}
                </StatusBadge>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
              <div className="text-center">
                <p className={`text-3xl font-bold ${scoreTone(detail.score, detail.passScore)}`}>
                  {detail.score}
                </p>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.score")}</p>
              </div>
              <div className="text-center">
                <p className={`text-xl font-semibold ${gradeColor(detail.grade)}`}>
                  {detail.grade ?? "-"}
                </p>
                <p className="text-xs text-muted-foreground">{t("evaluationDetail.grade")}</p>
              </div>
              {/* C4-4:AI 离线兜底分标记——分数是安慰基础分,非真实成绩,与 H5 一致标注防经理误读达标率。 */}
              {detail.lowConfidence && (
                <StatusBadge type="warning">{t("evaluationDetail.lowConfidence")}</StatusBadge>
              )}
              {/* 及格线:与任务详情抽屉的及格率口径、H5 结果卡同一个常量 */}
              <p
                data-testid="eval-pass-line"
                className="ml-auto self-end text-xs text-muted-foreground tabular-nums"
              >
                {t("evaluationDetail.passLine", { score: detail.passScore ?? PASS_SCORE })}
              </p>
            </div>
            <DimensionScores raw={detail.dimensions} />
            {/*
              V33:两段可执行反馈。经理看成绩单是为了知道「这人该补什么」——
              只有一个分数和一句总评时,这张抽屉打开等于白开。
            */}
            <FeedbackList
              label={t("evaluationDetail.highlights")}
              lines={detail.highlights}
              accentClass="border-l-success"
            />
            <FeedbackList
              label={t("evaluationDetail.improvements")}
              lines={detail.improvements}
              accentClass="border-l-warning"
            />
            {detail.feedback && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("evaluationDetail.feedback")}
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{detail.feedback}</p>
              </div>
            )}
            <TurnScoreList label={t("evaluationDetail.turnScores")} rows={detail.turnScores} />
          </div>
        ) : isError ? (
          // audit-0713 C1c:出错不再永久转圈
          <QueryErrorBanner isError className="my-6" onRetry={() => void refetch()} />
        ) : (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
